import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"
import exifr from "exifr"
export default class extends Controller {
  static targets = [
    "canvas",
    "input",
    "resizableDiv",
    "captureDateText",
    "lockedIcon",
    "unlockedIcon",
    "cameraFileInput",
    "uploadFileInput",
  ]

  connect() {
    this.index_id = this.scope.element.dataset.indexId
    this.model = this.scope.element.dataset.modelName
    this.canvas = this.canvasTarget
    this.ctx = this.canvas.getContext("2d")
    this.input = this.inputTarget
    this.loadInitialImage()
    // Always start with canvas locked (file inputs enabled)
    this.lockCanvas()
  }

  loadInitialImage() {
    if (this.index_id != undefined) {
      const base64String = document.getElementById(
        `${this.model}_${this.model}_images_attributes_${this.index_id}_image`,
      ).value
      const captureDate = document.getElementById(
        `${this.model}_${this.model}_images_attributes_${this.index_id}_captured_at`,
      )?.value
      if (base64String != "") {
        this.currentImage = new Image()
        this.currentImage.src = base64String
        captureDate && this.updateCaptureDate(captureDate)
        this.currentImage.onload = () => {
          this.drawImage()
        }
      }
    }
  }

  async set_image(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()

    const captureDate = await this.getImageCaptureDate(file)
    const captureDateInput = document.getElementById(
      `${this.model}_${this.model}_images_attributes_${this.index_id}_captured_at`,
    )

    if (captureDateInput) {
      captureDateInput.value = captureDate
      this.updateCaptureDate(captureDate)
    }

    reader.onload = () => {
      const base64String = reader.result.split(",")[1]
      const prefixedBase64String = "data:image/png;base64," + base64String
      document.getElementById(
        `${this.model}_${this.model}_images_attributes_${this.index_id}_image`,
      ).value = prefixedBase64String

      this.currentImage = new Image()
      this.currentImage.src = prefixedBase64String
      this.currentImage.onload = () => {
        this.initializeSignaturePad()
        this.drawImage()
      }
    }
    reader.readAsDataURL(file)
  }

  drawImage() {
    if (!this.currentImage) return

    this.canvas.height = this.canvas.offsetHeight
    this.canvas.width = this.canvas.offsetWidth
    const hRatio = this.canvas.width / this.currentImage.width
    const vRatio = this.canvas.height / this.currentImage.height
    const ratio = Math.min(hRatio, vRatio)
    const centerShift_x = (this.canvas.width - this.currentImage.width * ratio) / 2
    const centerShift_y = (this.canvas.height - this.currentImage.height * ratio) / 2
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.ctx.drawImage(
      this.currentImage,
      0,
      0,
      this.currentImage.width,
      this.currentImage.height,
      centerShift_x,
      centerShift_y,
      this.currentImage.width * ratio,
      this.currentImage.height * ratio,
    )
  }

  initializeSignaturePad() {
    this.signaturePad = new SignaturePad(this.canvas, {
      penColor: "rgb(66, 133, 244)",
    })
    this._forceIOSTouchEvents()

    this.signaturePad.addEventListener("endStroke", () => {
      this.input.value = this.signaturePad.toDataURL()
    })
  }

  _forceIOSTouchEvents() {
    const isIOS =
      /iPhone|iPad|iPod/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
    if (isIOS && window.PointerEvent) {
      this.signaturePad.off()
      const origPointerEvent = window.PointerEvent
      window.PointerEvent = undefined
      this.signaturePad.on()
      window.PointerEvent = origPointerEvent
    }
  }

  undo() {
    if (!this.signaturePad) return

    const data = this.signaturePad.toData()
    this.signaturePad.clear()
    const canvas = this.canvasTarget

    if (this.currentImage) {
      const originalImage = new Image()
      originalImage.src = this.currentImage.src

      originalImage.onload = () => {
        // Clear the canvas and redraw only the original image
        this.ctx.clearRect(0, 0, canvas.width, canvas.height)

        const hRatio = canvas.width / originalImage.width
        const vRatio = canvas.height / originalImage.height
        const ratio = Math.min(hRatio, vRatio)
        const centerShift_x = (canvas.width - originalImage.width * ratio) / 2
        const centerShift_y = (canvas.height - originalImage.height * ratio) / 2

        this.ctx.drawImage(
          originalImage,
          0,
          0,
          originalImage.width,
          originalImage.height,
          centerShift_x,
          centerShift_y,
          originalImage.width * ratio,
          originalImage.height * ratio,
        )

        data.pop()
        this.signaturePad.fromData(data, { clear: false })
        this.inputTarget.value = this.signaturePad.toDataURL()
      }
    }
  }

  async getImageCaptureDate(file) {
    const resolveFallback = () => {
      if (file.lastModified) {
        const fileDate = new Date(file.lastModified)
        return fileDate.toISOString().replace("T", " ").split(".")[0]
      }
      return new Date().toISOString().replace("T", " ").split(".")[0]
    }

    try {
      const exifData = await exifr.parse(file, [
        "DateTimeOriginal",
        "DateTime",
        "DateTimeDigitized",
      ])

      if (exifData) {
        const captureDate =
          exifData.DateTimeOriginal || exifData.DateTime || exifData.DateTimeDigitized

        if (captureDate instanceof Date) {
          const pad = (n) => String(n).padStart(2, "0")
          return `${captureDate.getFullYear()}-${pad(captureDate.getMonth() + 1)}-${pad(captureDate.getDate())} ${pad(captureDate.getHours())}:${pad(captureDate.getMinutes())}:${pad(captureDate.getSeconds())}`
        }
      }
    } catch (e) {
      console.warn("EXIF parsing failed, using fallback date:", e.message)
    }

    return resolveFallback()
  }

  updateCaptureDate(date) {
    if (this.hasCaptureDateTextTarget) {
      const [extractedDate, time] = date.includes("T") ? date.split("T") : date.split(" ")
      const [year, month, day] = extractedDate.split("-")
      const [hour, min] = time.split(":")
      const formattedDate = `${day}.${month}.${year} - ${hour}:${min}`
      this.captureDateTextTarget.innerText = formattedDate
    }
  }

  disconnect() {
    if (this.signaturePad) {
      this.signaturePad.off()
    }
  }

  lockCanvas() {
    if (this.signaturePad) {
      this.signaturePad.on()
      this._forceIOSTouchEvents()
    }
    this.unlockedIconTarget.classList.contains("hidden") &&
      this.unlockedIconTarget.classList.remove("hidden")
    !this.lockedIconTarget.classList.contains("hidden") &&
      this.lockedIconTarget.classList.add("hidden")
    if (this.hasCameraFileInputTarget) {
      this.cameraFileInputTarget.disabled = false
    }
    if (this.hasUploadFileInputTarget) {
      this.uploadFileInputTarget.disabled = false
    }
  }

  unlockCanvas() {
    this.signaturePad && this.signaturePad.off()
    this.lockedIconTarget.classList.contains("hidden") &&
      this.lockedIconTarget.classList.remove("hidden")
    !this.unlockedIconTarget.classList.contains("hidden") &&
      this.unlockedIconTarget.classList.add("hidden")
    if (this.hasCameraFileInputTarget) {
      this.cameraFileInputTarget.disabled = true
    }
    if (this.hasUploadFileInputTarget) {
      this.uploadFileInputTarget.disabled = true
    }
  }
}
